import React, { useEffect, useState } from 'react';
import { recouvrementService } from '../../api/services';
import { BalanceAgee } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Suivi des impayés (TF-010) : balance âgée des factures clients non soldées,
 * ventilée par ancienneté. Les clients avec litige sont signalés.
 */
export function Impayes() {
  const [data, setData] = useState<BalanceAgee | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    recouvrementService.getBalanceAgee().then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <LoadingSpinner />;

  const t = data?.totaux;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Impayés — balance âgée</h1>
        <p className="page-subtitle">Encours clients ventilé par ancienneté</p>
      </div>

      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {[
            ['Non échu', t.nonEchu], ['1-30 j', t.b1a30], ['31-60 j', t.b31a60],
            ['61-90 j', t.b61a90], ['+90 j', t.plus90], ['Total', t.total],
          ].map(([label, val]) => (
            <div key={label as string} className="card">
              <p className="text-xs text-gray-500">{label}</p>
              <MoneyDisplay amount={val as number} size="sm" className="font-semibold" />
            </div>
          ))}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-500 uppercase">
              <th className="px-4 py-2">Client</th>
              <th className="px-4 py-2 text-right">Non échu</th>
              <th className="px-4 py-2 text-right">1-30 j</th>
              <th className="px-4 py-2 text-right">31-60 j</th>
              <th className="px-4 py-2 text-right">61-90 j</th>
              <th className="px-4 py-2 text-right">+90 j</th>
              <th className="px-4 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.lignes ?? []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                Aucun impayé 🎉
              </td></tr>
            )}
            {data?.lignes.map((l) => (
              <tr key={l.clientId}>
                <td className="px-4 py-2">
                  {l.clientRaisonSociale}
                  {l.litige && <span className="ml-2 text-xs text-red-600">⚠ litige</span>}
                </td>
                <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.nonEchu} size="sm" /></td>
                <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.b1a30} size="sm" /></td>
                <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.b31a60} size="sm" /></td>
                <td className="px-4 py-2 text-right text-amber-700"><MoneyDisplay amount={l.b61a90} size="sm" /></td>
                <td className="px-4 py-2 text-right text-red-700"><MoneyDisplay amount={l.plus90} size="sm" /></td>
                <td className="px-4 py-2 text-right font-medium"><MoneyDisplay amount={l.total} size="sm" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Impayes;
